/*
  lx-menu.js — Behaviour for the native mega menu (desktop) + drawer drill-down
  (mobile). Replaces the Meteor Mega Menu runtime.

  Desktop: hover-intent (100ms open / 300ms close grace), menubar keyboard
  pattern (roving focus, ArrowDown/Enter/Space open, Escape close), ARIA
  aria-expanded sync, focus return. No open/close animation (instant).

  Mobile: drill-down panels that slide in from the right; a Back control slides
  them out; body scroll-lock + drawer shell are handled by Dawn. Respects
  prefers-reduced-motion (CSS disables the slide; JS still works instantly).
*/
(function () {
  'use strict';

  var FOCUSABLE_CARD = '.lx-mm__card';

  /* ====================================================================== */
  /* DESKTOP MEGA MENU                                                       */
  /* ====================================================================== */
  function initDesktop() {
    var nav = document.querySelector('.lx-mm');
    if (!nav) return;

    var list = nav.querySelector('.lx-mm__list');
    var triggers = Array.prototype.slice.call(nav.querySelectorAll('[data-lx-mm-trigger]'));
    var panelsByName = {};
    Array.prototype.slice.call(nav.querySelectorAll('[data-lx-mm-panel]')).forEach(function (p) {
      panelsByName[p.getAttribute('data-lx-mm-panel')] = p;
    });

    // Visible top-level menubar items (Search is display:none -> excluded).
    var menubarItems = Array.prototype.slice
      .call(list.querySelectorAll('[role="menuitem"]'))
      .filter(function (el) {
        var li = el.closest('.lx-mm__item');
        return !(li && li.classList.contains('lx-mm__item--search'));
      });

    var openTimer = null;
    var closeTimer = null;
    var openName = null;

    function panelOf(name) {
      return panelsByName[name];
    }
    function triggerOf(name) {
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].getAttribute('data-lx-mm-trigger') === name) return triggers[i];
      }
      return null;
    }

    function doOpen(name) {
      window.clearTimeout(closeTimer);
      triggers.forEach(function (t) {
        var n = t.getAttribute('data-lx-mm-trigger');
        var isOn = n === name;
        t.setAttribute('aria-expanded', isOn ? 'true' : 'false');
        var p = panelOf(n);
        if (p) p.setAttribute('data-open', isOn ? 'true' : 'false');
      });
      openName = name;
    }

    function doCloseAll() {
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
        var p = panelOf(t.getAttribute('data-lx-mm-trigger'));
        if (p) p.setAttribute('data-open', 'false');
      });
      openName = null;
    }

    function scheduleOpen(name) {
      window.clearTimeout(closeTimer);
      window.clearTimeout(openTimer);
      openTimer = window.setTimeout(function () {
        doOpen(name);
      }, 100);
    }
    function scheduleClose() {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(doCloseAll, 300);
    }
    function cancelTimers() {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    }

    // ---- Hover intent (trigger + its panel form one hover group) ----
    triggers.forEach(function (t) {
      var name = t.getAttribute('data-lx-mm-trigger');
      var li = t.closest('.lx-mm__item');
      var panel = panelOf(name);

      if (li) {
        li.addEventListener('mouseenter', function () {
          scheduleOpen(name);
        });
        li.addEventListener('mouseleave', function () {
          scheduleClose();
        });
      }
      if (panel) {
        panel.addEventListener('mouseenter', function () {
          cancelTimers();
          if (openName !== name) doOpen(name);
        });
        panel.addEventListener('mouseleave', function () {
          scheduleClose();
        });
      }

      // ---- Click / touch toggles ----
      t.addEventListener('click', function (event) {
        event.preventDefault();
        cancelTimers();
        if (openName === name) {
          doCloseAll();
        } else {
          doOpen(name);
          if (event.detail === 0) focusFirstCard(name); // keyboard-activated click
        }
      });
    });

    function focusFirstCard(name) {
      var panel = panelOf(name);
      if (!panel) return;
      var first = panel.querySelector(FOCUSABLE_CARD);
      if (first) first.focus();
    }

    // ---- Menubar roving focus + open/close keys ----
    var activeIndex = 0;
    function setRoving(index) {
      menubarItems.forEach(function (el, i) {
        el.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      activeIndex = index;
    }
    setRoving(0);

    function focusItem(index) {
      var n = menubarItems.length;
      var i = ((index % n) + n) % n;
      setRoving(i);
      menubarItems[i].focus();
    }

    menubarItems.forEach(function (item, index) {
      item.addEventListener('focus', function () {
        setRoving(index);
      });
      item.addEventListener('keydown', function (event) {
        var key = event.key;
        var name = item.getAttribute('data-lx-mm-trigger');
        if (key === 'ArrowRight') {
          event.preventDefault();
          focusItem(index + 1);
        } else if (key === 'ArrowLeft') {
          event.preventDefault();
          focusItem(index - 1);
        } else if (key === 'Home') {
          event.preventDefault();
          focusItem(0);
        } else if (key === 'End') {
          event.preventDefault();
          focusItem(menubarItems.length - 1);
        } else if (key === 'Escape') {
          if (openName) {
            event.preventDefault();
            doCloseAll();
          }
        } else if (name && (key === 'ArrowDown' || key === 'Enter' || key === ' ' || key === 'Spacebar')) {
          event.preventDefault();
          doOpen(name);
          focusFirstCard(name);
        }
      });
    });

    // ---- Panel keyboard: arrow-navigate cards, Escape returns to trigger ----
    triggers.forEach(function (t) {
      var name = t.getAttribute('data-lx-mm-trigger');
      var panel = panelOf(name);
      if (!panel) return;
      var cards = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_CARD));

      panel.addEventListener('keydown', function (event) {
        var key = event.key;
        var current = document.activeElement;
        var idx = cards.indexOf(current);
        if (key === 'Escape') {
          event.preventDefault();
          doCloseAll();
          t.focus();
        } else if (key === 'ArrowRight' || key === 'ArrowDown') {
          if (idx > -1) {
            event.preventDefault();
            cards[Math.min(idx + 1, cards.length - 1)].focus();
          }
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          if (idx > -1) {
            event.preventDefault();
            cards[Math.max(idx - 1, 0)].focus();
          }
        } else if (key === 'Home') {
          event.preventDefault();
          if (cards[0]) cards[0].focus();
        } else if (key === 'End') {
          event.preventDefault();
          if (cards.length) cards[cards.length - 1].focus();
        }
      });
    });

    // Close when focus leaves the whole nav (e.g. Tab out).
    nav.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!nav.contains(document.activeElement)) doCloseAll();
      }, 0);
    });

    // Close on click / tap outside the nav (touch + mouse).
    document.addEventListener('click', function (event) {
      if (openName && !nav.contains(event.target)) doCloseAll();
    });
  }

  /* ====================================================================== */
  /* MOBILE DRAWER DRILL-DOWN                                                */
  /* ====================================================================== */
  function initDrawer() {
    var root = document.querySelector('[data-lx-drawer]');
    if (!root) return;

    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-lx-drawer-trigger]'));
    var panelsByName = {};
    Array.prototype.slice.call(root.querySelectorAll('[data-lx-drawer-panel]')).forEach(function (p) {
      panelsByName[p.getAttribute('data-lx-drawer-panel')] = p;
    });
    var rootList = root.querySelector('.lx-drawer__root');
    var activeName = null;

    function panelOf(name) {
      return panelsByName[name];
    }
    function triggerOf(name) {
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].getAttribute('data-lx-drawer-trigger') === name) return triggers[i];
      }
      return null;
    }

    function openPanel(name) {
      var panel = panelOf(name);
      if (!panel) return;
      var trigger = triggerOf(name);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('data-active', '');
      root.setAttribute('data-active', name);
      // Grow the drawer so the Dawn footer sits below the panel (matches Meteor).
      root.style.height = panel.scrollHeight + 'px';
      if (rootList) rootList.setAttribute('aria-hidden', 'true');
      activeName = name;
      // move focus to the Back control
      var back = panel.querySelector('[data-lx-drawer-back]');
      if (back) back.focus();
    }

    function closePanel(focusTrigger) {
      if (!activeName) return;
      var name = activeName;
      var panel = panelOf(name);
      var trigger = triggerOf(name);
      if (panel) panel.removeAttribute('data-active');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      root.removeAttribute('data-active');
      root.style.height = '';
      if (rootList) rootList.removeAttribute('aria-hidden');
      activeName = null;
      if (focusTrigger && trigger) trigger.focus();
    }

    triggers.forEach(function (t) {
      var name = t.getAttribute('data-lx-drawer-trigger');
      function activate(event) {
        event.preventDefault();
        openPanel(name);
      }
      t.addEventListener('click', activate);
      t.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          activate(event);
        }
      });
    });

    root.querySelectorAll('[data-lx-drawer-back]').forEach(function (back) {
      function goBack(event) {
        event.preventDefault();
        closePanel(true);
      }
      back.addEventListener('click', goBack);
      back.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          goBack(event);
        }
      });
    });

    // Escape inside a drill-down closes the panel (not the whole drawer).
    // Dawn's MenuDrawer closes the whole drawer on the bubbling `keyup`, so we
    // intercept `keyup` in the capture phase and stopPropagation to pre-empt it.
    root.addEventListener(
      'keyup',
      function (event) {
        if (event.key === 'Escape' && activeName) {
          event.stopPropagation();
          event.preventDefault();
          closePanel(true);
        }
      },
      true
    );
    // Swallow the matching keydown too, so nothing else acts on it.
    root.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && activeName) {
          event.preventDefault();
        }
      },
      true
    );

    // Reset to the root list whenever the drawer itself is closed, so it
    // reopens on the root next time.
    var details = root.closest('details');
    if (details) {
      details.addEventListener('toggle', function () {
        if (!details.open && activeName) closePanel(false);
      });
    }
  }

  function init() {
    initDesktop();
    initDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
